import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CharsPerLineInput.scss';
import { TextInput } from '../../FormComponents/TextInput/TextInput';
import { CaptionModeEnum } from '../../../types/enums/CaptionModeEnum';
import useProject from '../../../hooks/useProject';
import Help from '../../Help/Help';

const CharsPerLineInput = () => {
  const { t } = useTranslation();
  const { updateEncoder, updateStream, project } = useProject();
  const [ charsPerLine, setCharsPerLine ] = useState<number>(project.encoder.aggregatorCharsPerLine);
  const deboucne = 100;
  const min = 10; 
  const openMaxChars = 64;
  const closedMaxChars = 32;

  const max = (): number => {
    switch (project.encoder.captionMode) {
      case CaptionModeEnum.None:
      case CaptionModeEnum.Closed:
      default:
        return closedMaxChars;
      case CaptionModeEnum.Open:
        return openMaxChars;
    }
  };

  const onAfterChange = (data: string) => {
    if (project.encoder) {
      let value = charsPerLine;

      if (value < min) {
        value = min;
      }
      else {
        if (value > max()) {
          value = max();
        }
      }

      setCharsPerLine(value);

      if (project.stream?.sourceLanguageIsoCode === project.encoder?.languageIsoCode) {
        updateStream({
          aggregatorCharsPerLine: value,
        }, deboucne);
      } else {
        updateEncoder(project.encoder, {
          aggregatorCharsPerLine: value,
        }, deboucne);
      }
    }
  };

  useEffect(() => {
    setCharsPerLine(project.encoder.aggregatorCharsPerLine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.encoder.aggregatorCharsPerLine]);

  return (
    <div className="chars-per-line-input">
      <div className="input-title">
        {t('producer.inputs.chars-per-line.title')}
        <Help
          title="Characters Per Line"
          text="Characters Per Line determines the maximum number of characters displayed on a single line of captions.<br/><br/>- Fewer characters per line improve readability and prevent text from stretching across the screen.<br/>- More characters per line allow longer sentences to fit but may make captions harder to read quickly.<br/>- Note: Some players, like YouTube, may not display captions with more than 28 characters per line, while others like Vimeo can handle longer lines.<br/><br/>Adjust this setting to balance readability with player compatibility."
        />
      </div>
      <TextInput
        placeholder="producer.inputs.chars-per-line.placeholder"
        value={charsPerLine.toString()}
        onChange={(value) => {setCharsPerLine(parseInt(value))}}
        onAfterChange={onAfterChange}
        validate={false}
        number={true}
      />
      <button className="btn btn-apply btn-secondary btn-no-margin btn-margin-right" type="button">Apply</button>
      <div className="input-component-footer">{t('producer.inputs.chars-per-line.footer')}</div>
    </div>
  );
};

export default React.memo(CharsPerLineInput);
